import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';
import {
  EllipsisVerticalIcon,
  SquarePenIcon,
  Trash2Icon,
  UserRoundPlusIcon,
  UserRoundXIcon,
  UsersRoundIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import { Invitation } from '@/shared/types/member';
import { Role } from '@/shared/types/role';
import { Team } from '@/shared/types/team';
import { COLUMN_PRESETS } from '@/util/constants';

interface TeamWithActions extends Team {
  onEditTeam?: () => void;
  onAddMembers?: () => void;
  onRemoveMembers?: () => void;
  onDeleteTeam?: () => void;
  canWrite?: boolean;
  numberOfInvitations?: number;
}

interface ColumnsProps {
  roles: Role[];
  accountInvitations: Invitation[];
  canWrite: boolean;
  onEditTeam: (team: Team) => void;
  onAddMembers: (team: Team) => void;
  onRemoveMembers: (team: Team) => void;
  onDeleteTeam: (team: Team) => void;
}

export const createColumns = ({
  roles,
  accountInvitations,
  canWrite,
  onEditTeam,
  onAddMembers,
  onRemoveMembers,
  onDeleteTeam,
}: ColumnsProps): ColumnDef<TeamWithActions>[] => {
  return [
    {
      accessorKey: 'name',
      ...COLUMN_PRESETS.FLEX,
      meta: { preset: 'FLEX', className: 'pl-6' },
      header: ({ column }) => <SortableHeader column={column}>{t('common.name')}</SortableHeader>,
      cell: ({ row }) => {
        const team = row.original;
        const { member_count, team_id } = team;
        const numberOfInvitations =
          accountInvitations?.filter((inv) => inv.team_ids.includes(team_id)).length ?? 0;

        const hasRoles = team.role_ids.length > 0;

        return (
          <div className="flex items-center gap-2">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full ${
                hasRoles
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground'
              }`}
            >
              <UsersRoundIcon size={16} />
            </div>
            <div className="flex min-w-0 flex-col">
              <span className="truncate">{team.name}</span>
              <span className="truncate text-xs leading-4 text-muted-foreground">
                {t('teams.members_count', { 0: member_count })}
                {numberOfInvitations > 0 && ` • ${numberOfInvitations} invitations`}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'description',
      header: t('common.description'),
      enableSorting: false,
      ...COLUMN_PRESETS.LG,
      meta: { preset: 'LG' },
      cell: ({ row }) => {
        const description = row.original.description;
        return <div className="truncate">{description || '-'}</div>;
      },
    },
    {
      accessorKey: 'role_ids',
      header: t('teams.roles'),
      enableSorting: false,
      ...COLUMN_PRESETS.LG,
      meta: { preset: 'LG' },
      cell: ({ row }) => {
        const roleIds: string[] = row.original.role_ids;
        const roleNames = roleIds?.map((role_id: string) => {
          const name = roles.find((role) => role.role_id === role_id)?.name;
          return name;
        });
        return <div className="truncate">{roleNames?.join(', ') || '-'}</div>;
      },
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      ...COLUMN_PRESETS.SM,
      meta: { preset: 'SM' },
      cell: ({ row }) => {
        const team = row.original;
        const isDisabled = !canWrite;

        return (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost-neutral" size="icon-sm" disabled={isDisabled}>
                <EllipsisVerticalIcon size={16} />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => onAddMembers(team)}
                className="flex items-center gap-2"
              >
                <UserRoundPlusIcon size={16} className="text-muted-foreground" />
                {`${t('teams.add_members')}…`}
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => onRemoveMembers(team)}
                disabled={team.member_count === 0}
                className="flex items-center gap-2"
              >
                <UserRoundXIcon size={16} className="text-muted-foreground" />
                {`${t('teams.remove_members')}…`}
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => onEditTeam(team)}
                className="flex items-center gap-2"
              >
                <SquarePenIcon size={16} className="text-muted-foreground" />
                {`${t('teams.edit')}…`}
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => onDeleteTeam(team)}
                variant="destructive"
                className="flex items-center gap-2"
              >
                <Trash2Icon size={16} />
                {`${t('teams.delete')}…`}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];
};
